import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Truck, CheckCircle, Clock, AlertCircle, 
  Filter, Download, Eye, Edit, Search, X, RefreshCw, DollarSign,
  BarChart3, Users, CreditCard, MapPin, Phone, Calendar,
  ChevronLeft, ChevronRight, MoreVertical, ArrowUpDown
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function OrderManagement({ isMobile, user }) {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    delivered: 0,
    revenue: 0,
    avgOrder: 0
  });
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "getOrders",
        page: currentPage,
        limit: itemsPerPage,
        filters: filters
      });
      
      if (response.data.success) {
        setOrders(response.data.orders);
        setStats(response.data.stats || {
          total: response.data.orders.length,
          pending: response.data.orders.filter(o => o.status === 'pending').length,
          processing: response.data.orders.filter(o => o.status === 'processing').length,
          delivered: response.data.orders.filter(o => o.status === 'delivered').length,
          revenue: response.data.orders.reduce((sum, o) => sum + o.total, 0),
          avgOrder: response.data.orders.length > 0 ? response.data.orders.reduce((sum, o) => sum + o.total, 0) / response.data.orders.length : 0
        });
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      // Mock data for demo
      const mockOrders = Array.from({ length: 25 }, (_, i) => ({
        id: `ORD-${1000 + i}`,
        customer_name: ['John Restaurant', 'Jane Cafe', 'Mike Hotel', 'Sarah Catering'][i % 4],
        total: 145.99 + (i * 25),
        status: ['pending', 'processing', 'delivered', 'cancelled'][i % 4],
        created_at: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
        payment_status: ['paid', 'pending', 'paid', 'failed'][i % 4],
        item_count: 2 + (i % 8),
        delivery_option: i % 2 === 0 ? 'delivery' : 'pickup',
        phone: '+263 78' + (3907172 + i),
        address: 'Zvido Business Centre, Chitungwiza',
        email: `customer${i + 1}@example.com`,
        notes: i % 3 === 0 ? 'Special instructions: Cut into smaller pieces' : '',
        items: [
          { name: 'Premium Pork Leg', quantity: 2, price: 22.99, total: 45.98 },
          { name: 'Pork Shoulder', quantity: 1, price: 15.99, total: 15.99 },
          { name: 'Pork Belly', quantity: 3, price: 12.99, total: 38.97 }
        ].slice(0, 1 + (i % 3))
      }));
      setOrders(mockOrders);
      setStats({
        total: mockOrders.length,
        pending: mockOrders.filter(o => o.status === 'pending').length,
        processing: mockOrders.filter(o => o.status === 'processing').length,
        delivered: mockOrders.filter(o => o.status === 'delivered').length,
        revenue: mockOrders.reduce((sum, o) => sum + o.total, 0),
        avgOrder: mockOrders.reduce((sum, o) => sum + o.total, 0) / mockOrders.length
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = searchQuery === '' || 
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.phone.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilters = Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(order[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
    
    return matchesSearch && matchesFilters;
  });

  // Sorting
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (sortConfig.key === 'created_at') {
      return sortConfig.direction === 'asc' 
        ? new Date(a[sortConfig.key]) - new Date(b[sortConfig.key])
        : new Date(b[sortConfig.key]) - new Date(a[sortConfig.key]);
    }
    if (sortConfig.key === 'total') {
      return sortConfig.direction === 'asc' 
        ? a[sortConfig.key] - b[sortConfig.key]
        : b[sortConfig.key] - a[sortConfig.key];
    }
    if (sortConfig.key === 'customer_name') {
      return sortConfig.direction === 'asc'
        ? a[sortConfig.key].localeCompare(b[sortConfig.key])
        : b[sortConfig.key].localeCompare(a[sortConfig.key]);
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);
  const paginatedOrders = sortedOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "updateOrderStatus",
        orderId: orderId,
        status: newStatus
      });

      if (response.data.success) {
        await fetchOrders();
        setStatusUpdate(null);
        // Success animation
        const successEl = document.getElementById(`order-${orderId}`);
        if (successEl) {
          successEl.classList.add('animate-pulse');
          setTimeout(() => successEl.classList.remove('animate-pulse'), 500);
        }
      } else {
        alert('Failed to update order status.');
      }
    } catch (error) {
      console.error('Update status error:', error);
      alert('Failed to update order status.');
    }
  };

  const handleExport = (exportType) => {
    try {
      const exportData = filteredOrders.map((order, index) => ({
        'SN': index + 1,
        'Order ID': order.id,
        'Customer': order.customer_name,
        'Date': order.created_at,
        'Total': `$${order.total.toFixed(2)}`,
        'Status': order.status,
        'Payment Status': order.payment_status,
        'Items': order.item_count,
        'Phone': order.phone,
        'Delivery': order.delivery_option
      }));

      if (exportType === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Orders");
        XLSX.writeFile(wb, `orders_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('Orders Export - Sir Henry\'s Butchery', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        
        const tableData = exportData.map(item => [
          item.SN,
          item['Order ID'],
          item.Customer,
          item.Date,
          item.Total,
          item.Status,
          item['Payment Status'],
          item.Items
        ]);

        doc.autoTable({
          startY: 25,
          head: [['SN', 'Order ID', 'Customer', 'Date', 'Total', 'Status', 'Payment Status', 'Items']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`orders_export_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Orders exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export orders.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'from-emerald-500 to-teal-500';
      case 'processing': return 'from-blue-500 to-cyan-500';
      case 'pending': return 'from-amber-500 to-orange-500';
      case 'cancelled': return 'from-red-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'processing': return <Package className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'cancelled': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            Order Management
          </h2>
          <p className="text-gray-400">Manage and track all customer orders</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchOrders}
            className="px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Refresh</span>
          </motion.button>
          
          <div className="flex space-x-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Orders', value: stats.total, icon: Package, color: 'blue', change: '+12%' },
          { label: 'Pending', value: stats.pending, icon: Clock, color: 'amber', change: '-3%' },
          { label: 'Processing', value: stats.processing, icon: Truck, color: 'purple', change: '+5%' },
          { label: 'Delivered', value: stats.delivered, icon: CheckCircle, color: 'emerald', change: '+8%' },
          { label: 'Revenue', value: `$${stats.revenue.toFixed(2)}`, icon: DollarSign, color: 'green', change: '+15%' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getStatusColor(stat.color)} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${stat.change.startsWith('+') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                {stat.change}
              </span>
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
            <div className="text-sm text-gray-400">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders by ID, customer, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
          
          <div className="flex gap-3">
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <select
              value={filters.payment_status || ''}
              onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
              className="px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg"
            >
              <option value="">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending Payment</option>
              <option value="failed">Failed</option>
            </select>
            
            <button
              onClick={() => setFilters({})}
              className="px-4 py-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-800 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded ${viewMode === 'table' ? 'bg-gray-700' : ''}`}
              >
                <BarChart3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 rounded ${viewMode === 'cards' ? 'bg-gray-700' : ''}`}
              >
                <Package className="w-5 h-5" />
              </button>
            </div>
            
            <div className="text-sm text-gray-400">
              Showing {paginatedOrders.length} of {filteredOrders.length} orders
            </div>
          </div>
          
          <div className="text-sm text-gray-400">
            Avg Order: <span className="text-emerald-400 font-bold">${stats.avgOrder.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Orders Display */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-pink-500 border-b-transparent rounded-full animate-spin animation-delay-1000"></div>
          </div>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onViewDetails={setSelectedOrder}
              onUpdateStatus={setStatusUpdate}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">
                    <button onClick={() => handleSort('id')} className="flex items-center gap-1">
                      Order ID
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">
                    <button onClick={() => handleSort('customer_name')} className="flex items-center gap-1">
                      Customer
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">
                    <button onClick={() => handleSort('created_at')} className="flex items-center gap-1">
                      Date
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">
                    <button onClick={() => handleSort('total')} className="flex items-center gap-1">
                      Total
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Payment</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {paginatedOrders.map((order) => (
                  <tr key={order.id} id={`order-${order.id}`} className="hover:bg-gray-900/30">
                    <td className="px-6 py-4">
                      <div className="font-bold text-white">{order.id}</div>
                      <div className="text-xs text-gray-500">{order.item_count} items</div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-white">{order.customer_name}</div>
                        <div className="text-xs text-gray-500">{order.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-lg font-bold text-white">${order.total.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getStatusColor(order.status)} flex items-center gap-1 w-fit`}>
                        {getStatusIcon(order.status)}
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${order.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setStatusUpdate({ orderId: order.id, currentStatus: order.status })}
                          className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredOrders.length > 0 && (
        <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 bg-gray-900/50 rounded-2xl border border-gray-800">
          <div className="text-sm text-gray-400 mb-4 md:mb-0">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-700 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-white">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-700 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-white">Order Details</h3>
                    <p className="text-gray-400">#{selectedOrder.id}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Order Summary */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Order Items */}
                    <div className="bg-gray-900/50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-white mb-4">Order Items</h4>
                      <div className="space-y-3">
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="font-medium text-white">{item.name}</div>
                                <div className="text-sm text-gray-400">Qty: {item.quantity}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-white">${item.total.toFixed(2)}</div>
                              <div className="text-sm text-gray-400">${item.price} each</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="bg-gray-900/50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-white mb-4">Customer Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-400">Customer Name</label>
                          <div className="flex items-center gap-2 mt-1">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.customer_name}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-400">Phone Number</label>
                          <div className="flex items-center gap-2 mt-1">
                            <Phone className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.phone}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-400">Email Address</label>
                          <div className="flex items-center gap-2 mt-1">
                            <CreditCard className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.email || 'N/A'}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-400">Order Date</label>
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.created_at}</span>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-sm text-gray-400">Delivery Address</label>
                          <div className="flex items-center gap-2 mt-1">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.address}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Status */}
                  <div className="space-y-6">
                    <div className="bg-gray-900/50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-white mb-4">Order Summary</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Status</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getStatusColor(selectedOrder.status)}`}>
                            {selectedOrder.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Payment</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${selectedOrder.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {selectedOrder.payment_status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Items</span>
                          <span className="text-white">{selectedOrder.item_count}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Delivery</span>
                          <span className="text-white capitalize">{selectedOrder.delivery_option}</span>
                        </div>
                        <div className="pt-3 border-t border-gray-800">
                          <div className="flex items-center justify-between text-lg font-bold">
                            <span className="text-gray-400">Total</span>
                            <span className="text-white">${selectedOrder.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() => setStatusUpdate({ orderId: selectedOrder.id, currentStatus: selectedOrder.status })}
                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                      >
                        Update Status
                      </button>
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="w-full py-3 border border-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-800 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Update Modal */}
      <AnimatePresence>
        {statusUpdate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 w-full max-w-md"
            >
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Update Order Status</h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Select New Status</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { status: 'pending', label: 'Pending', icon: Clock, color: 'from-amber-500 to-orange-500' },
                        { status: 'processing', label: 'Processing', icon: Package, color: 'from-blue-500 to-cyan-500' },
                        { status: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'from-emerald-500 to-teal-500' },
                        { status: 'cancelled', label: 'Cancelled', icon: AlertCircle, color: 'from-red-500 to-pink-500' }
                      ].map((option) => (
                        <motion.button
                          key={option.status}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleUpdateStatus(statusUpdate.orderId, option.status)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            statusUpdate.currentStatus === option.status
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-gray-700 hover:border-purple-500/50'
                          }`}
                        >
                          <div className={`w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-r ${option.color} flex items-center justify-center`}>
                            <option.icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-sm font-medium text-white capitalize">{option.label}</div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setStatusUpdate(null)}
                    className="w-full py-3 border border-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OrderCard({ order, onViewDetails, onUpdateStatus, getStatusColor, getStatusIcon }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden"
    >
      <div className="p-6">
        {/* Order Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">#{order.id}</h3>
            <p className="text-sm text-gray-400">{order.created_at}</p>
          </div>
          <div className="relative">
            <button className="p-1 hover:bg-gray-800 rounded-lg">
              <MoreVertical className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-white font-medium">{order.customer_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-500" />
            <span className="text-gray-400 text-sm">{order.phone}</span>
          </div>
        </div>

        {/* Order Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Items</span>
            <span className="text-white font-bold">{order.item_count}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Total</span>
            <span className="text-xl font-bold text-white">${order.total.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Payment</span>
            <span className={`text-sm ${order.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}`}>
              {order.payment_status}
            </span>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mb-6">
          <span className={`px-3 py-2 rounded-full text-xs font-semibold bg-gradient-to-r ${getStatusColor(order.status)} flex items-center justify-center gap-1`}>
            {getStatusIcon(order.status)}
            {order.status}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onViewDetails(order)}
            className="py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Details
          </button>
          <button
            onClick={() => onUpdateStatus({ orderId: order.id, currentStatus: order.status })}
            className="py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Update
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default OrderManagement;